// Shared helpers for showing a trail system's billing state in the UI.
// The server owns `trailSystem.billing`; these only describe it.
const DAY = 86400000

export const TIER_NAMES = {
  square: 'Square',
  diamond: 'Diamond',
  doubleDiamond: 'Double Diamond',
  circle: 'Circle',
  triangle: 'Triangle',
  star: 'Star'
}

export function toDate (v) {
  if (!v) return null
  if (v instanceof Date) return v
  if (typeof v.toDate === 'function') return v.toDate()
  if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000)
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export function fmtDate (v) {
  const d = toDate(v)
  return d ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''
}

export function daysUntil (v, now = new Date()) {
  const d = toDate(v)
  return d ? Math.ceil((d.getTime() - now.getTime()) / DAY) : null
}

export function tierName (tier) {
  return TIER_NAMES[tier] || tier || 'No plan'
}

// Returns { status, label, variant, detail, action, urgent }
export function describeBilling (ts, now = new Date()) {
  const b = (ts && ts.billing) || null
  if (!b) {
    if (ts && ts.override) return { status: 'override', label: 'Complimentary', variant: 'info', detail: 'This trail system is covered by TrailHUB.', action: null, urgent: false }
    if (ts && ts.blocked) return { status: 'paused', label: 'Paused', variant: 'danger', detail: 'Choose a plan to make the public map live again.', action: 'reactivate', urgent: true }
    if (ts && ts.subscription) return { status: 'active', label: tierName(ts.subscription), variant: 'success', detail: 'Active plan.', action: null, urgent: false }
    return { status: 'unknown', label: 'Not evaluated yet', variant: 'secondary', detail: 'Billing status will appear after the next check.', action: null, urgent: false }
  }
  switch (b.status) {
    case 'trial': {
      const left = daysUntil(b.trialEndsAt, now)
      return {
        status: 'trial',
        label: left <= 0 ? 'Trial ending' : `Free trial · ${left} day${left === 1 ? '' : 's'} left`,
        variant: left <= 7 ? 'warning' : 'info',
        detail: `Your free trial ends ${fmtDate(b.trialEndsAt)}. Choose a plan to keep the public map live.`,
        action: 'choose',
        urgent: left <= 7
      }
    }
    case 'active':
      return {
        status: 'active',
        label: `${tierName(b.tier)} plan`,
        variant: 'success',
        detail: b.cancelAt
          ? `Your plan is set to end on ${fmtDate(b.cancelAt)}. Resume it from Manage billing to keep the map live.`
          : (b.renewsAt ? `Renews ${fmtDate(b.renewsAt)}.` : 'Active plan.'),
        action: b.cancelAt ? 'manage' : null,
        urgent: Boolean(b.cancelAt)
      }
    case 'grace': {
      const left = daysUntil(b.graceEndsAt, now)
      return {
        status: 'grace',
        label: 'Payment failed',
        variant: 'danger',
        detail: `We couldn't collect your last payment. Update your card by ${fmtDate(b.graceEndsAt)} (${left} day${left === 1 ? '' : 's'}) or this trail system will be paused.`,
        action: 'manage',
        urgent: true
      }
    }
    case 'paused':
      return {
        status: 'paused',
        label: 'Paused',
        variant: 'danger',
        detail: b.reason === 'grace_expired'
          ? 'Payment could not be collected, so the public map is paused. Update your payment method or choose a plan to reactivate.'
          : (b.reason === 'subscription_ended' ? 'Your subscription ended, so the public map is paused. Choose a plan to reactivate.' : 'Your free trial ended, so the public map is paused. Choose a plan to reactivate.'),
        action: b.reason === 'grace_expired' ? 'manage' : 'choose',
        urgent: true
      }
    case 'seasonal':
      return b.reason === 'scheduled_start'
        ? { status: 'seasonal', label: `Starts ${fmtDate(b.resumesAt)}`, variant: 'info', detail: `Your ${tierName(b.tier)} plan${b.price ? ` (${b.price})` : ''} starts ${fmtDate(b.resumesAt)}. Visitors see a "closed for the season" notice until then.`, action: 'seasonal', urgent: false }
        : { status: 'seasonal', label: 'Paused for the season', variant: 'info', detail: `Billing is paused until ${fmtDate(b.resumesAt)}, when your ${tierName(b.tier)} plan${b.price ? ` (${b.price})` : ''} restarts automatically. Visitors see a "closed for the season" notice until then.`, action: 'seasonal', urgent: false }
    case 'legacy':
      return { status: 'legacy', label: 'No plan', variant: 'warning', detail: 'This trail system has no plan yet. Choose a plan to keep it live.', action: 'choose', urgent: false }
    case 'override':
      return { status: 'override', label: `Complimentary · ${tierName(b.tier)}`, variant: 'info', detail: 'This trail system is covered by TrailHUB.', action: null, urgent: false }
    default:
      return { status: 'unknown', label: 'Unknown', variant: 'secondary', detail: '', action: null, urgent: false }
  }
}

export function isPaused (ts) {
  if (!ts) return false
  if (ts.override) return false
  if (ts.billing && ts.billing.status) return ['paused', 'seasonal'].includes(ts.billing.status)
  return Boolean(ts.blocked)
}
